import React, { useMemo, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';
import clsx from 'clsx';
import { getMyProfile } from '../../services/api';
import { useActiveClient } from '../../contexts/ActiveClientContext';
import { useAsync } from '../aai/useAsync';
import { ErrorNote, Loading, Panel } from '../../components/aa';
import { ScopingPluginSection } from './ScopingPluginSection';
// import FrameworkTabs from './FrameworkTabs';

/*
 * Settings for the Solution Partner themselves: who they are, the framework
 * their clients inherit, and the Orbit scoping plugin they work with.
 *
 * The framework here is the partner's own version and carries no client, which
 * is why it belongs under their name. One client's version is a different
 * thing and is reached from that client, under their Settings.
 *
 * Nothing else here describes a client. A client's boundary, grants, people, roles,
 * plan and access history live on that client's Overview under Settings, so a
 * partner with twenty clients never reads one client's facts under their own
 * name. Nothing here needs a client selected either: a partner with no clients
 * yet can still connect Orbit.
 */

type SectionId = 'profile' | 'plugin';

const SECTIONS: { id: SectionId; label: string; keywords: string }[] = [
  { id: 'profile', label: 'Profile', keywords: 'name email account who' },
  // The Intake framework now sits in the sidebar at /partner/framework.
  // { id: 'framework', label: 'Intake framework', keywords: 'framework questions intake dossier opening starter fields document registry' },
  { id: 'plugin', label: 'Scoping plugin (Orbit)', keywords: 'claude connector download mcp orbit' },
];

interface MyProfile {
  name: string | null;
  email: string | null;
  phone: string | null;
}

// The profile endpoint answers for the signed-in person and is not scoped to a
// client, so it works before the partner has any client at all.
const ProfileSection: React.FC = () => {
  const profile = useAsync<MyProfile>(
    () => getMyProfile().then((data: MyProfile) => ({ data })), 'Could not load your profile.', [],
  );
  if (profile.loading) return <Loading label="Loading your profile" />;
  if (profile.error || !profile.data) return <ErrorNote message={profile.error || 'Could not load your profile.'} />;
  const name = profile.data.name || localStorage.getItem('user_name') || 'Solution Partner';
  const email = profile.data.email || localStorage.getItem('user_email') || '';
  return (
    <Panel>
      <div className="flex flex-wrap items-center gap-5 px-5 py-6 md:px-6">
        <span className="grid h-14 w-14 shrink-0 place-items-center rounded-tile bg-ink text-[18px] font-bold text-white">
          {initialsOf(name)}
        </span>
        <div className="min-w-0">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">Solution Partner</div>
          <div className="mt-0.5 text-[22px] font-bold leading-tight text-ink">{name}</div>
          <div className="mt-0.5 text-[13px] text-ink-dim">{email}</div>
          {profile.data.phone && <div className="text-[13px] text-ink-dim">{profile.data.phone}</div>}
        </div>
      </div>
    </Panel>
  );
};

const initialsOf = (name: string): string => {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

export const Intro: React.FC<{ title: string; body: string }> = ({ title, body }) => (
  <div>
    <h2 className="m-0 text-[15px] font-bold text-ink">{title}</h2>
    <p className="m-0 mt-1 text-[13px] leading-[21px] text-ink-dim">{body}</p>
  </div>
);

const PartnerSettings: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Settings opens over whatever page sent the partner here, so closing goes
  // back there. Without an origin, the Clients list is home.
  const returnTo = (location.state as { from?: string } | null)?.from || '/partner/clients';
  const [params, setParams] = useSearchParams();
  const { loading: clientsLoading } = useActiveClient();
  const [query, setQuery] = useState('');

  const requested = params.get('section') as SectionId | null;
  const sectionFrom = (raw: SectionId | null): SectionId => {
    if (raw && SECTIONS.some((s) => s.id === raw)) return raw;
    return 'profile';
  };
  const section = sectionFrom(requested);
  const goSection = (id: SectionId) => setParams({ section: id }, { replace: true });

  const userName = localStorage.getItem('user_name') || 'Solution Partner';
  const userEmail = localStorage.getItem('user_email') || '';

  const shown = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return SECTIONS;
    return SECTIONS.filter((s) => `${s.label} ${s.keywords}`.toLowerCase().includes(needle));
  }, [query]);

  const close = () => navigate(returnTo);
  const label = SECTIONS.find((s) => s.id === section)?.label || 'Settings';

  if (clientsLoading) {
    return (
      <div className="fixed inset-0 z-[200] grid place-items-center bg-ink/25 backdrop-blur-sm">
        <div className="rounded-panel bg-white px-8"><Loading label="Opening settings" /></div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center bg-ink/30 backdrop-blur-sm"
      onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}
    >
      {/* The console's large-modal size: 1238 x 754 capped by the viewport,
          full bleed under sm. Same shell as Orbit Control, because two big
          modals at two sizes reads as an accident. */}
      <div className="flex h-full min-h-0 w-full overflow-hidden bg-white sm:h-[min(754px,calc(100vh-2rem))] sm:w-[min(1238px,calc(100vw-2rem))] sm:rounded-panel">
        <aside className="hidden w-[268px] shrink-0 flex-col border-r border-line bg-canvas-inset px-5 pb-5 pt-6 lg:flex">
          <div className="flex items-center gap-3">
            <span className="grid h-10 w-10 shrink-0 place-items-center rounded-tile bg-ink text-[13px] font-bold text-white">
              {initialsOf(userName)}
            </span>
            <div className="min-w-0">
              <div className="text-[15px] font-bold leading-tight text-ink">Settings</div>
              <div className="truncate text-[12.5px] text-ink-dim">Your account and tools</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
              Search settings
            </div>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-soft" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search settings"
                className="h-11 w-full rounded-card border-2 border-ink bg-white pl-9 pr-3 text-[13px] text-ink outline-none placeholder:text-ink-soft"
              />
            </div>
          </div>

          <nav className="mt-4 flex-1 space-y-0.5 overflow-y-auto">
            {shown.map((item) => {
              const on = item.id === section;
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => goSection(item.id)}
                  className={clsx(
                    'flex h-11 w-full items-center gap-3 rounded-card px-4 text-left text-[13.5px] transition-colors',
                    on && 'bg-white font-bold text-ink shadow-sm',
                    !on && 'font-medium text-ink-muted hover:bg-white/60 hover:text-ink',
                  )}
                >
                  <span className="flex-1">{item.label}</span>
                  {on && <span aria-hidden className="text-ink-soft">→</span>}
                </button>
              );
            })}
            {shown.length === 0 && (
              <p className="m-0 px-4 py-3 text-[12.5px] text-ink-dim">No setting matches that.</p>
            )}
          </nav>

          <div className="mt-4 flex items-center gap-3 border-t border-line pt-4">
            <span className="grid h-9 w-9 shrink-0 place-items-center rounded-tile bg-ink text-[12px] font-bold text-white">
              {initialsOf(userName)}
            </span>
            <div className="min-w-0">
              <div className="truncate text-[13px] font-bold text-ink">{userName}</div>
              <div className="truncate text-[12px] text-ink-dim">{userEmail || 'Solution Partner'}</div>
            </div>
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col">
          <header className="flex shrink-0 items-start justify-between gap-4 border-b border-line px-6 py-5 md:px-8">
            <div className="min-w-0">
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-eyebrow">
                Solution Partner
              </div>
              <h1 className="m-0 mt-1.5 text-[26px] font-bold leading-tight tracking-[-0.015em] text-ink">
                {label}
              </h1>
            </div>
            <button
              type="button"
              onClick={close}
              aria-label="Close settings"
              className="grid h-10 w-10 shrink-0 place-items-center rounded-full border border-line bg-white text-ink-muted transition-colors hover:bg-canvas-inset hover:text-ink"
            >
              <X size={17} />
            </button>
          </header>

          {/* Below lg the rail is gone, so the sections move into a select. */}
          <div className="shrink-0 border-b border-line px-6 py-3 lg:hidden">
            <select
              value={section}
              onChange={(e) => goSection(e.target.value as SectionId)}
              aria-label="Settings section"
              className="h-11 w-full rounded-act border border-line-strong bg-white px-3 text-[13px] font-semibold text-ink"
            >
              {SECTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </select>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-6 md:px-8">
            {section === 'profile' && <ProfileSection />}

            {/* {section === 'framework' && (
              <div className="space-y-4">
                <Intro
                  title="Intake framework"
                  body="What a customer is asked when they self-register through your link, and the intake every client then goes through. Every client you add inherits this. To change one client only, open that client and edit it under their own Settings."
                />
                <FrameworkTabs clientId="" clientName="" />
              </div>
            )} */}

            {section === 'plugin' && (
              <div className="space-y-4">
                <Intro
                  title="Scoping plugin (Orbit)"
                  body="Orbit's scoping tools inside Claude. Connect once through the address below, or download a personal copy for Claude Code."
                />
                <ScopingPluginSection />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PartnerSettings;
